import { ArrowLeftIcon, EyeIcon } from "@heroicons/react/24/outline";
import { enumToCheckboxItems } from "@utils/utils";
import React, { useEffect, useRef, useState } from "react";
import { useDebounce } from "use-debounce";

import { Button, Headline, Input, Paragraph } from "@fronterahealth/frontera-ui-components";

import {
  ApiClinicListingVerificationStatusChoices,
  ClinicalServiceType,
  ModalityType,
} from "@api/graphql/types-and-hooks";
import {
  CreateClinicListingOutput,
  ModalityTypesEnum,
  PayorsEnum,
  ServicesOfferedEnum,
  useCareNavigationClinicListingCreate,
  useCareNavigationUpdateClinicListingCreate,
  useGoogleMapsAutocompleteRetrieve,
  useGooglePlaceMetadataRetrieve,
} from "@api/rest/types-and-hooks";
import { useFormUtils } from "@components/forms/useFormUtils";
import { notifyError } from "@components/notifications/notifications";

interface AddClinicFormProps {
  setShowAddClinicForm: React.Dispatch<React.SetStateAction<boolean>>;
  setPendingClinc?: React.Dispatch<React.SetStateAction<CreateClinicListingOutput | undefined>>;
  initialValues?: Partial<ClinicFormValues>;
  clinicListingId?: string;
}

interface ClinicFormValues {
  name: string;
  address: string;
  phone: string;
  website: string;
  email: string;
  about: string;
  services: string[];
  modalities: string[];
  payors: string[];
}

export const AddEditClinicForm: React.FC<AddClinicFormProps> = ({
  setShowAddClinicForm,
  setPendingClinc,
  initialValues,
  clinicListingId,
}) => {
  const isEditing = !!clinicListingId;
  const [addressSearchTerm, setAddressSearchTerm] = useState<string>("");
  const [selectedPlaceId, setSelectedPlaceId] = useState<string>("");
  const [debouncedAddressSearchTerm] = useDebounce(addressSearchTerm, 200);
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
  const isAddressSelectedRef = useRef<boolean>(false);

  const { data: autocompleteData } = useGoogleMapsAutocompleteRetrieve(
    { search_term: debouncedAddressSearchTerm },
    { query: { enabled: !!debouncedAddressSearchTerm } },
  );

  const { data: placeData } = useGooglePlaceMetadataRetrieve(
    { place_id: selectedPlaceId },
    { query: { enabled: !!selectedPlaceId } },
  );

  const addressResults = autocompleteData?.status === 200 ? autocompleteData.data : undefined;
  const placeMetadata = placeData?.status === 200 ? placeData.data : undefined;

  const { mutateAsync: createClinicListing } = useCareNavigationClinicListingCreate();
  const { mutateAsync: updateClinicListing } = useCareNavigationUpdateClinicListingCreate();

  const {
    formState,
    onSubmit,
    register,
    setValue,
    watch,
    reset,
    RegisteredFormInput,
    RegisteredFormTextArea,
    RegisteredCheckboxList,
  } = useFormUtils<ClinicFormValues>({
    useNativeValidation: false,
    mutationFn: async (values) => {
      if (isEditing) {
        const response = await updateClinicListing({
          data: {
            public_listing_id: clinicListingId,
            name: values.name,
            description: values.about || null,
            website_url: values.website || null,
            phone_number: values.phone || null,
            contact_email: values.email || null,
            services_offered: values.services as ServicesOfferedEnum[],
            modality_types: values.modalities as ModalityTypesEnum[],
            payors: values.payors as PayorsEnum[],
            full_address: values.address || null,
          },
        });

        if (response.status !== 200) {
          notifyError("Failed to update clinic. Please try again.");
          return;
        }
        setShowAddClinicForm(false);
        return;
      }

      const response = await createClinicListing({
        data: {
          name: values.name,
          description: values.about || null,
          website_url: values.website || null,
          phone_number: values.phone || null,
          contact_email: values.email || null,
          full_address: values.address || null,
          services_offered: values.services as ServicesOfferedEnum[],
          modality_types: values.modalities as ModalityTypesEnum[],
          payors: values.payors as PayorsEnum[],
          with_organization: true,
          verification_status: ApiClinicListingVerificationStatusChoices.Pending,
        },
      });

      if (response.status !== 201) {
        notifyError(response.data.detail || "Failed to create clinic. Please try again.");
        return;
      }
      setShowAddClinicForm(false);

      if (setPendingClinc) {
        setPendingClinc(response.data);
      }
    },
    defaultValues: {
      name: "",
      address: "",
      phone: "",
      website: "",
      email: "",
      about: "",
      services: [],
      modalities: [],
      payors: [],
    },
  });

  const {
    onChange: rhfAddressOnChange,
    ref: addressRef,
    name: addressName,
    onBlur: addressOnBlur,
  } = register("address", {
    required: "Address is required",
    validate: () => isAddressSelectedRef.current || "Please select a valid address from the suggestions",
  });

  const addressValue = watch("address") ?? "";
  const addressError = formState.errors["address"]?.message?.toString();

  // set the validated address after place selection
  useEffect(() => {
    if (!placeMetadata?.formatted_address) return;

    isAddressSelectedRef.current = true;
    setValue("address", placeMetadata.formatted_address, { shouldValidate: true });
    setAddressSearchTerm(placeMetadata.formatted_address);
    setShowSuggestions(false);
  }, [placeMetadata]);

  // populate form with initial values for edit mode
  useEffect(() => {
    if (!initialValues) return;
    reset({ ...initialValues });
    if (initialValues.address) {
      setAddressSearchTerm(initialValues.address);
      isAddressSelectedRef.current = true;
    }
  }, [initialValues]);

  // close suggestions on outside click
  const addressWrapperRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (addressWrapperRef.current && !addressWrapperRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const watchedServices = watch("services") ?? [];
  const watchedModalities = watch("modalities") ?? [];
  const watchedPayors = watch("payors") ?? [];

  return (
    <div className="flex flex-col gap-6 pt-4 wrap-break-word">
      {!isEditing && (
        <button
          onClick={() => setShowAddClinicForm(false)}
          className="flex flex-row items-center gap-2 w-fit hover:opacity-90 transition-opacity"
        >
          <ArrowLeftIcon className="size-4 text-rio-600" style={{ strokeWidth: 2 }} />
          <Paragraph className="text-rio-600!" displayType="loud" size="small">
            Back to search
          </Paragraph>
        </button>
      )}

      <form onSubmit={onSubmit}>
        <div className="flex flex-row gap-6">
          <div className="flex flex-col gap-6 overflow-y-auto">
            <div className="flex flex-col p-8 border bg-white rounded-xl">
              <Headline className="pb-2">Basic Information</Headline>

              <RegisteredFormInput
                formKey="name"
                formState={formState}
                label="Clinic name"
                placeholder="Harborview ABA Center"
              />

              <div ref={addressWrapperRef} className="relative mb-4">
                <Input
                  name={addressName}
                  ref={addressRef}
                  label="Address"
                  variant="dropdown"
                  inputSize="full"
                  value={addressValue}
                  onBlur={addressOnBlur}
                  onChange={(e) => {
                    rhfAddressOnChange(e);
                    setAddressSearchTerm(e.target.value);
                    isAddressSelectedRef.current = false;
                    setShowSuggestions(true);
                  }}
                  placeholder="1915 Chopper St. Denver, CO 80202"
                  formNoValidate={showSuggestions}
                  errorMessage={!showSuggestions ? addressError : undefined}
                />
                {showSuggestions && addressResults && addressResults.results.length > 0 && (
                  <div className="absolute left-0 right-0 z-10 flex flex-col gap-1 py-2 bg-white border rounded-md">
                    {addressResults.results.map((result) => (
                      <div
                        className="cursor-pointer hover:bg-limestone-50"
                        key={result.place_id}
                        onMouseDown={() => setSelectedPlaceId(result.place_id)}
                      >
                        <div className="px-2">
                          <Paragraph colorType="primary" size="small">
                            {result.address}
                          </Paragraph>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex flex-row gap-4">
                <RegisteredFormInput
                  formKey="phone"
                  formState={formState}
                  label="Phone number"
                  placeholder="(123) 456-7890"
                  inputSize="sm"
                />
                <RegisteredFormInput
                  formKey="website"
                  formState={formState}
                  label="Website"
                  placeholder="harborviewaba.com"
                  inputSize="sm"
                />
              </div>

              <RegisteredFormInput
                formKey="email"
                formState={formState}
                label="Lead contact email"
                placeholder="leads@harborviewaba.com"
                helpText="Where should we send lead notifications?"
              />

              <RegisteredFormTextArea
                formKey="about"
                formState={formState}
                label="About your clinic"
                required={false}
                placeholder="We provide comprehensive ABA therapy services for children with autism and developmental delays. Our experienced team uses evidence-based practices in a warm, supportive environment."
                rows={4}
              />
            </div>

            <div className="flex flex-col p-8 border bg-white rounded-xl">
              <Headline>Services offered</Headline>

              <RegisteredCheckboxList
                formKey="services"
                formState={formState}
                title="Services"
                orientation="vertical"
                items={enumToCheckboxItems(ClinicalServiceType)}
              />
            </div>

            <div className="flex flex-col p-8 border bg-white rounded-xl">
              <Headline>Service details</Headline>
              <RegisteredCheckboxList
                formKey="modalities"
                formState={formState}
                title="Modalities"
                orientation="vertical"
                items={enumToCheckboxItems(ModalityType)}
              />
            </div>

            <div className="flex flex-col p-8 border bg-white rounded-xl">
              <Headline>Insurance accepted</Headline>
              <RegisteredCheckboxList
                formKey="payors"
                formState={formState}
                title="Insurance"
                orientation="vertical"
                items={enumToCheckboxItems(PayorsEnum)}
              />
            </div>
            <div className="flex flex-row gap-2 mt-2 justify-end">
              <Button
                text="Cancel"
                variant="primary"
                buttonStyle="border"
                onClick={() => setShowAddClinicForm(false)}
              />
              <Button text={isEditing ? "Save" : "Submit"} onClick={onSubmit} />
            </div>
          </div>

          <div className="flex flex-col w-[25vw]">
            <div className="sticky top-20">
              <div className="flex flex-col gap-4 p-8 border bg-white rounded-xl">
                <div className="flex flex-row items-center gap-2">
                  <EyeIcon className="size-4 text-text-secondary" />
                  <Paragraph displayType="loud" colorType="primary">
                    Preview for families
                  </Paragraph>
                </div>

                <div className="flex flex-col">
                  <Headline>{watch("name")}</Headline>
                  <Paragraph size="small" colorType="secondary">
                    {addressValue}
                  </Paragraph>
                </div>

                <Paragraph size="medium" colorType="primary">
                  {watch("about")}
                </Paragraph>

                {watchedServices.length > 0 && (
                  <div className="flex flex-col gap-1">
                    <Paragraph displayType="loud" colorType="primary">
                      Services
                    </Paragraph>
                    <div className="flex flex-row gap-2 flex-wrap">
                      {watchedServices.map((service) => (
                        <div key={service} className="p-2 rounded-3xl bg-limestone-50">
                          <Paragraph colorType="secondary">
                            {enumToCheckboxItems(ClinicalServiceType).find((i) => i.id === service)?.title}
                          </Paragraph>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {watchedModalities.length > 0 && (
                  <div className="flex flex-col gap-1">
                    <Paragraph displayType="loud" colorType="primary">
                      Modalities
                    </Paragraph>
                    <div className="flex flex-row gap-2 flex-wrap">
                      {watchedModalities.map((modality) => (
                        <div key={modality} className="p-2 rounded-3xl bg-limestone-50">
                          <Paragraph colorType="secondary">
                            {enumToCheckboxItems(ModalityType).find((i) => i.id === modality)?.title}
                          </Paragraph>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {watchedPayors.length > 0 && (
                  <div className="flex flex-col gap-1">
                    <Paragraph displayType="loud" colorType="primary">
                      Insurance accepted
                    </Paragraph>
                    <div className="flex flex-row gap-2 flex-wrap">
                      {watchedPayors.map((payor) => (
                        <div key={payor} className="p-2 rounded-3xl bg-limestone-50">
                          <Paragraph colorType="secondary">
                            {enumToCheckboxItems(PayorsEnum).find((i) => i.id === payor)?.title}
                          </Paragraph>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};
